import { FC, ReactNode } from 'react';
import {
  expectedHeader,
  HeaderBarRule,
  headerBarRule,
  headerLines
} from '../../helpers/fontChangesHelpers';
import { XrayAnalysisState } from '../../hooks/useXrayAnalysis';
import {
  FontCheckHowTo,
  FontCheckLabel,
  HeaderBarBox,
  HeaderBarExpected,
  HeaderBarPage,
  HeaderBarRow,
  HeaderBarSign,
  HeaderBarText,
  ListCheckEmptyState
} from '../../styles';
import { listCheckCopy } from './listCheckCopy';
import { messageOf, TextSide } from './TextPropertiesCompare';

interface HeaderBarsTabProps {
  documentXray?: XrayAnalysisState;
  pdfProvider?: string | null;
}

const escape = (char: string) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const withBars = (text: string, rule: HeaderBarRule): ReactNode[] =>
  text
    .split(new RegExp(`(${escape(rule.expected)}|${escape(rule.retyped)})`))
    .map((part, index) => {
      if (part !== rule.expected && part !== rule.retyped) return part;
      const retyped = part === rule.retyped;
      return (
        <HeaderBarSign
          key={index}
          $bad={retyped}
          data-testid={retyped ? 'font-changes-pipe' : undefined}
        >
          {part}
        </HeaderBarSign>
      );
    });

const HeaderBarsTab: FC<HeaderBarsTabProps> = ({ documentXray, pdfProvider }) => {
  const copy = listCheckCopy.fontChanges;
  const rule = headerBarRule(pdfProvider);

  if (!rule) {
    return (
      <div data-testid="font-changes-header-bars">
        <ListCheckEmptyState>{copy.noHeaderBarRule}</ListCheckEmptyState>
      </div>
    );
  }

  const lines = headerLines(documentXray?.data?.text_breakups?.spans, rule);
  const side: TextSide = {
    title: listCheckCopy.thisDocument,
    status: documentXray?.status ?? 'idle',
    data: documentXray?.data ?? null,
    onRetry: documentXray?.retry
  };
  const message = messageOf(side, !lines.length, copy.noHeaderLine);
  const retyped = lines.some((line) => line.text.includes(rule.retyped));

  return (
    <div data-testid="font-changes-header-bars">
      <FontCheckHowTo data-testid="font-changes-header-how-to">
        {withBars(`${copy.headerBarsRule(rule.bank, rule.firstPage, rule.expected)} `, rule)}
        {copy.headerBarsHowTo(rule.firstPage)}
      </FontCheckHowTo>
      {message ? (
        <ListCheckEmptyState>{message}</ListCheckEmptyState>
      ) : (
        <HeaderBarBox>
          <FontCheckLabel>{copy.expectedStatement}</FontCheckLabel>
          <HeaderBarExpected>
            <HeaderBarText $muted data-testid="font-changes-expected-header">
              {withBars(expectedHeader(lines[0].text, rule), rule)}
            </HeaderBarText>
          </HeaderBarExpected>
          <FontCheckLabel $bad={retyped}>{listCheckCopy.thisDocument}</FontCheckLabel>
          {lines.map((line) => (
            <HeaderBarRow key={line.page}>
              <HeaderBarPage data-testid="font-changes-header-page">
                {listCheckCopy.page(line.page)}
              </HeaderBarPage>
              <HeaderBarText data-testid="font-changes-header-line">
                {withBars(line.text, rule)}
              </HeaderBarText>
            </HeaderBarRow>
          ))}
        </HeaderBarBox>
      )}
    </div>
  );
};

export default HeaderBarsTab;
